/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material"
import { useEffect, useState } from "react"

interface ColumnConfig {
  label: string
  value: string
  align?: 'left' | 'center' | 'right'
  formatValue?: (value: any) => string
  width?: string | number // Nueva prop para configurar ancho
}

interface TotalValues {
  label: string;
  key: string;
  value: number;
}

interface DataListV2Props<T> {
  data: T[]
  columns: ColumnConfig[]
  onItemClick?: (item: T, index: number) => void
  showTotal?: boolean
  totalFields?: TotalValues[]
  formatTotal?: (value: any) => string
  isSelected?: (item: T, index: number) => boolean
}

export function DataListV2<T extends Record<string, any>>({
  data,
  columns,
  onItemClick,
  showTotal = true,
  totalFields,
  formatTotal = (val) => val?.toString() || "0",
  isSelected,
}: DataListV2Props<T>) {

  const [sumTotals, setSumTotals] = useState<number[]>([])

  useEffect(() => {
    if (!totalFields || !totalFields.length || !showTotal) {
      return;
    }
    let totals: number[] = [];
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      for (let ii = 0; ii < totalFields.length; ii++) {
        const value = parseFloat(`${totals[ii] || 0}`) + parseFloat(`${row[totalFields[ii].key] || 0}`);
        totals[ii] = value;
      }
    }
    setSumTotals(totals)
  }, [data, totalFields])

  const hasCustomWidths = columns.some(col => col.width)

  return (
    <Paper
      elevation={2}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "var(--card)",
        overflow: "hidden", // Contener el contenido
      }}
    >
      <TableContainer 
        sx={{ 
          flex: 1, 
          overflow: "auto", // Scroll tanto vertical como horizontal
          borderBottom: showTotal ? "1px solid var(--border)" : "none",
        }}
      >
        <Table 
          stickyHeader 
          size="small"
          sx={{
            // Si hay widths personalizados, la tabla ocupa el 100%
            width: hasCustomWidths ? '100%' : 'auto',
            // Forzar el layout de la tabla para respetar los widths
            tableLayout: hasCustomWidths ? 'fixed' : 'auto',
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell 
                  key={index}
                  sx={{ 
                    padding: "8px 16px",
                    backgroundColor: "white",
                    color: "black",
                    fontWeight: 700,
                    borderBottom: "1px solid var(--border)",
                    fontSize: "0.875rem",
                    textAlign: column.align || 'left',
                    // Aplicar width si está configurado
                    width: column.width || 'auto',
                    minWidth: column.width ? column.width : 'auto',
                    // Forzar el comportamiento del texto
                    overflow: 'hidden',
                    wordWrap: 'break-word',
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, rowIndex) => (
              <TableRow
                key={rowIndex}
                onClick={() => onItemClick?.(item, rowIndex)}
                sx={{
                  cursor: onItemClick ? "pointer" : "default",
                  backgroundColor: isSelected?.(item, rowIndex) ? "rgba(20, 124, 157, 0.1)" : "transparent",
                  color: isSelected?.(item, rowIndex) ? "#147c9d" : "var(--foreground)",
                  borderLeft: isSelected?.(item, rowIndex) ? "4px solid #147c9d" : "4px solid transparent",
                  transition: "all 0.2s ease",
                  "&:hover": onItemClick
                    ? {
                        backgroundColor: isSelected?.(item, rowIndex) ? "rgba(20, 124, 157, 0.15)" : "rgba(0, 0, 0, 0.04)",
                      }
                    : {},
                  "&:last-child td": {
                    borderBottom: "none"
                  }
                }}
              >
                {columns.map((column, colIndex) => (
                  <TableCell 
                    key={colIndex}
                    sx={{ 
                      padding: "8px 16px",
                      borderBottom: "1px solid var(--border)",
                      fontSize: "0.875rem",
                      textAlign: column.align || 'left',
                      fontWeight: column.align === 'right' ? 600 : 'normal',
                      // Aplicar width si está configurado
                      width: column.width || 'auto',
                      minWidth: column.width ? column.width : 'auto',
                      // Comportamiento del texto:
                      // Si hay width configurado, permitir wrap, sino ellipsis
                      overflow: 'hidden',
                      textOverflow: column.width ? 'clip' : 'ellipsis',
                      whiteSpace: column.width ? 'normal' : 'nowrap',
                      wordWrap: column.width ? 'break-word' : 'normal',
                      // Asegurar que el contenido haga wrap correctamente
                      wordBreak: 'break-word',
                      // Altura de línea para mejor legibilidad con wrap
                      lineHeight: '1.4',
                    }}
                  >
                    {column.formatValue 
                      ? column.formatValue(item[column.value])
                      : item[column.value]
                    }
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {showTotal && (
        <Box
          sx={{
            padding: "12px 16px",
            backgroundColor: "var(--primary)",
            color: "var(--primary-foreground)",
            fontWeight: 700,
            // Asegurar que el total también respete el scroll horizontal
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          {totalFields?.map((field, i) => 
            <Box
              key={`${i}`}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {field.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {sumTotals[i]}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  )
}